import { useQuery } from '@tanstack/react-query';
import {
  mapFromApiToOrganizationModel,
  mapFromApiToOrganizationAggregateModel,
  mapFromAPIToOrganizationProjectModel,
} from '@/models/organizations/map-from-api';
import { UseFetchParams } from '@/models/common.types';
import { OrganizationAggregatesReq } from '@/api/generated/model';
import {
  QUERY_KEY_ORGANIZATION_AGGREGATES,
  QUERY_KEY_ORGANIZATION_PROJECTS,
  QUERY_KEY_ORGANIZATIONS,
  QUERY_KEY_SAVED_FILTERS,
} from '@/constants/query-keys';
import {
  getAggregates as getOrganizationAggregates,
  getOrganizations,
} from '@/api/generated/organizations/organizations';
import { getFilterGroups } from '@/api/generated/projects/projects';

export function useFetchOrganizations() {
  return useQuery({
    queryKey: [QUERY_KEY_ORGANIZATIONS],
    queryFn: () => getOrganizations(),
    select: (data) => {
      return {
        organizations: data.organizations.map(mapFromApiToOrganizationModel),
        defaultFailureCategories: data.default_failure_categories,
      };
    },
  });
}

export function useFetchSavedFilters(projectId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY_SAVED_FILTERS, projectId],
    queryFn: () => getFilterGroups(projectId ?? '00000000-00000000-00000000-00000000'),
    enabled: !!projectId,
  });
}

export function useFetchOrganizationsAggregates({
  payload,
  enabled,
}: UseFetchParams<Omit<OrganizationAggregatesReq, 'group_by'>>) {
  return useQuery({
    queryKey: [QUERY_KEY_ORGANIZATION_AGGREGATES, payload, enabled],
    queryFn: ({ signal }) =>
      getOrganizationAggregates(
        {
          ...payload,
          group_by: { kind: 'organization' },
        },
        { signal },
      ),
    enabled,
    select: (data) => {
      return data.aggregates.map(mapFromApiToOrganizationAggregateModel);
    },
    keepPreviousData: true,
  });
}

export function useFetchOrganizationProjects({ payload, enabled }: UseFetchParams<OrganizationAggregatesReq>) {
  return useQuery({
    queryKey: [QUERY_KEY_ORGANIZATION_PROJECTS, payload, enabled],
    queryFn: ({ signal }) => getOrganizationAggregates(payload, { signal }),
    enabled,
    select: (data) => {
      return data.aggregates.map(mapFromAPIToOrganizationProjectModel);
    },
    keepPreviousData: true,
  });
}
